import { Table } from '@tanstack/react-table';

const PAGINATION_SLICE = 3;
const STATIC_OPERATORS = 4;
const START_DYNAMIC_PAGE = 3;

interface EndButton {
    onClick: () => void;
    disabled: boolean;
}

interface ActionButton {
    showValue: string;
    show: boolean;
    selected: boolean;
    onClick: () => void;
}

const useReactTablePagination = <T,>(table: Table<T>, sliceCount = PAGINATION_SLICE) => {
    const pagination = table.getState().pagination;
    const noHiding = table.getPageCount() <= STATIC_OPERATORS + sliceCount;
    const showSecondFromLeft = noHiding || pagination.pageIndex - sliceCount < 1;
    const showSecondFromRight =
        noHiding || pagination.pageIndex + sliceCount > table.getPageCount() - 1;

    const prevButtonProps: EndButton = {
        onClick: () => table.previousPage(),
        disabled: !table.getCanPreviousPage(),
    };
    const firstPageButtonProps: ActionButton = {
        show: true,
        showValue: '1',
        selected: pagination.pageIndex === 0,
        onClick: () => table.resetPageIndex(),
    };
    const secondPageButtonProps: ActionButton = {
        showValue: '2',
        show: table.getPageCount() > 1,
        selected: pagination.pageIndex === 1,
        onClick: () => showSecondFromLeft && table.setPageIndex(1),
    };
    const dynamicPageButtonProps: ActionButton[] = Array.from(Array(sliceCount).keys()).map(key => {
        const dynamicPage = pagination.pageIndex + key;
        const isLeftAdjacent =
            table.getPageCount() < sliceCount + STATIC_OPERATORS ||
            pagination.pageIndex < sliceCount + 1;
        const isRightAdjacent = pagination.pageIndex + sliceCount >= table.getPageCount() - 1;
        const leftAdjacentValue = key + START_DYNAMIC_PAGE;
        const rightAdjacentValue = key + table.getPageCount() - 1 - sliceCount;
        const value = isLeftAdjacent
            ? leftAdjacentValue
            : isRightAdjacent
              ? rightAdjacentValue
              : dynamicPage;

        return {
            show: value <= table.getPageCount(),
            showValue: value.toString(),
            selected: pagination.pageIndex === value - 1,
            onClick: () => table.setPageIndex(value - 1),
        };
    });
    const secondToLastPageButtonProps: ActionButton = {
        show: table.getPageCount() - 1 > sliceCount + 2,
        showValue: `${table.getPageCount() - 1}`,
        selected: pagination.pageIndex === table.getPageCount() - 2,
        onClick: () => showSecondFromRight && table.setPageIndex(table.getPageCount() - 2),
    };
    const lastPageButtonProps: ActionButton = {
        show: table.getPageCount() - 1 > sliceCount + 1,
        showValue: table.getPageCount().toString(),
        selected: pagination.pageIndex === table.getPageCount() - 1,
        onClick: () => table.setPageIndex(table.getPageCount() - 1),
    };
    const nextButtonProps: EndButton = {
        onClick: () => table.nextPage(),
        disabled: !table.getCanNextPage(),
    };

    return {
        showSecondFromLeft,
        showSecondFromRight,
        prevButtonProps,
        nextButtonProps,
        firstPageButtonProps,
        secondPageButtonProps,
        dynamicPageButtonProps,
        secondToLastPageButtonProps,
        lastPageButtonProps,
    };
};

export { PAGINATION_SLICE, useReactTablePagination };
export type { EndButton, ActionButton };
